import { useEffect, useState } from 'react';
import { FiAlertTriangle, FiChevronLeft, FiChevronRight, FiEye } from 'react-icons/fi';
import {
  Badge,
  Box,
  Center,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useAuthentication } from 'AuthContext';
import ScreenProtectedView from 'components/ScreenProtectedView/ScreenProtectedView';

function DocumentFrame({ url }) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const isPdf = url?.toLowerCase()?.includes('.pdf');

  if (isPdf) {
    return (
      <Box w='100%' h='70vh'>
        {isLoading && (
          <Center position='absolute' inset={0} zIndex={1}>
            <Spinner size='xl' color='blue.400' />
          </Center>
        )}
        <iframe
          src={`${url}#toolbar=0&navpanes=0&scrollbar=1`}
          title='KYC Document'
          width='100%'
          height='100%'
          style={{ border: 'none' }}
          onLoad={() => setIsLoading(false)}
          sandbox='allow-same-origin'
        />
      </Box>
    );
  }

  return (
    <Box w='100%' h='70vh' overflow='auto' display='flex' alignItems='center' justifyContent='center'>
      {isLoading && (
        <Center position='absolute' inset={0} zIndex={1}>
          <Spinner size='xl' color='blue.400' />
        </Center>
      )}
      {hasError ? (
        <Center h='100%'>
          <VStack>
            <FiEye size={32} color='gray' />
            <Text color='gray.500'>Unable to load document</Text>
          </VStack>
        </Center>
      ) : (
        <img
          src={url}
          alt='KYC Document'
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            userSelect: 'none',
            pointerEvents: 'none',
            WebkitUserDrag: 'none',
          }}
          draggable={false}
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setHasError(true);
          }}
        />
      )}
    </Box>
  );
}

export default function KycDocumentsModal({ isOpen, onClose, documents = [] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { user } = useAuthentication();

  const viewerIdentity = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Unknown User';

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
    }
  }, [isOpen]);

  const currentDoc = documents[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < documents.length - 1;

  if (!documents.length) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered size='lg'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>KYC Documents</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Center py={10}>
              <Text color='gray.500'>No KYC documents available for this merchant.</Text>
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='5xl' isCentered>
      <ModalOverlay bg='blackAlpha.700' />
      <ModalContent maxH='90vh'>
        <ModalHeader borderBottomWidth='1px'>
          <HStack spacing={3}>
            <Text>KYC Documents</Text>
            <Badge colorScheme='blue' fontSize='sm'>
              {currentIndex + 1} / {documents.length}
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={0} overflow='hidden'>
          <HStack bg='orange.50' px={4} py={2} spacing={2} borderBottomWidth='1px' borderColor='orange.200'>
            <FiAlertTriangle color='#C05621' size={14} />
            <Text fontSize='xs' color='orange.700'>
              Do not view these documents while screen sharing.
            </Text>
          </HStack>
          <ScreenProtectedView isActive={isOpen} viewerIdentity={viewerIdentity}>
            <DocumentFrame url={currentDoc?.url} key={currentDoc?.url} />
          </ScreenProtectedView>
          {documents.length > 1 && (
            <Flex justifyContent='center' alignItems='center' py={3} borderTopWidth='1px' gap={4}>
              <IconButton
                icon={<FiChevronLeft />}
                onClick={() => setCurrentIndex((i) => i - 1)}
                isDisabled={!hasPrev}
                aria-label='Previous document'
                size='sm'
                variant='ghost'
              />
              <Text fontSize='sm' color='gray.600'>
                {currentIndex + 1} / {documents.length}
              </Text>
              <IconButton
                icon={<FiChevronRight />}
                onClick={() => setCurrentIndex((i) => i + 1)}
                isDisabled={!hasNext}
                aria-label='Next document'
                size='sm'
                variant='ghost'
              />
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
